import React, { useContext,useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { OCLandInsightsContext } from "../../../../hooks/context/OcLandInsightsContext";
import {
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  Backdrop,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import Graphic from "@arcgis/core/Graphic";
import { omInspectionGisFieldsObject } from "../FieldObjects";
import {BoxApiUrl} from "../../../../apis/OcpwBoxDoc/OcpwBoxApi";
import axios from "axios";
import { removeFromSelectionHandler, selectDataHandler } from "../../select-form/spatial-select/spatialSelectVM";

const CreatePackageBtn = ({ payload, view }) => {
  const parentFolder='330175420648'

  const ocLandInsightsDictionaryCtx: any = useContext(OCLandInsightsContext);
  const { channelInspections, roadInspections } =
    ocLandInsightsDictionaryCtx.data || {};

  const [loading, setLoading] = useState(false); // State to track loading status
  const [dialogOpen, setDialogOpen] = useState(false); // State to track dialog open status
  const [dialogMessage, setDialogMessage] = useState<React.ReactNode>(""); // State to track dialog message
  const [error, setError] = useState(null); // State to track any errors
  const [packageId, setPackageId] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const onClick = async () => {
    let layerId: string | undefined = undefined;
    let objectIds: any[] = [];
    let targetPropertyName: any | undefined;
    let targetWorkCode: any | undefined;

    const {
      propertyName,
      workCode,
      objectId,
      packageId,
      status,
    } = omInspectionGisFieldsObject;

    const normalizeValue = (value: any) =>
      value === null || value === undefined ? "" : String(value).trim();

    setLoading(true);
    setError(null);

    if (!payload.length) {
      setLoading(false);
      setError("No payload data available.");
      return;
    }

    if (payload.length > 1) {
      setLoading(false);
      setError("Please only Select One Layer to create a Package.");
      return;
    }

    layerId = payload[0].layerId;

    const features = payload[0]?.features || [];
    console.log("Features received for package creation:", features);

    if (features.length < 1) {
      setLoading(false);
      setError("No valid features selected to create a package.");
      return;
    }

    targetPropertyName = normalizeValue(
      features[0].attributes[propertyName.name]
    );

    targetWorkCode = normalizeValue(
      features[0].attributes[workCode.name]
    );

    for (let i = 0; i < features.length; i++) {
      const feature = features[i];

      if (
        feature.parentCustomParameters?.OCLandInsightsID !== channelInspections &&
        feature.parentCustomParameters?.OCLandInsightsID !== roadInspections &&
        feature.parentLayerTitle !== "Pump Stations"
      ) {
        setLoading(false);
        setError("Please select a layer for Channel or Road Inspections.");
        return;
      }

      const currentPropertyName = normalizeValue(
        feature.attributes[propertyName.name]
      );

      const currentWorkCode = normalizeValue(
        feature.attributes[workCode.name]
      );

      if (currentPropertyName !== targetPropertyName) {
        setLoading(false);
        setError(
          "You selected more than one property. Please only create a package for one property at a time."
        );
        return;
      }

      if (currentWorkCode !== targetWorkCode) {
        setLoading(false);
        setError(
          "You selected more than one work code. Please only create a package for one work code at a time."
        );
        return;
      }

      for (const field of Object.values(omInspectionGisFieldsObject)) {
        const { name, mandatory } = field;

        if (
          mandatory &&
          (
            feature.attributes[name] === null ||
            feature.attributes[name] === undefined ||
            feature.attributes[name] === ""
          )
        ) {
          setLoading(false);
          setError(
            `Please fill in the mandatory field: ${name} for feature ${
              feature.attributes[objectId.name]
            }`
          );
          return;
        }
      }

      objectIds.push(feature.attributes[objectId.name]);
    }

    if (objectIds.length < 1) {
      setLoading(false);
      setError("No valid features selected to create a package.");
      return;
    }

    try {
      let featLayer = view.map.allLayers.find((layer) => layer.id === layerId);
      let uniqueId: any;

      if (!featLayer) {
        throw new Error("Layer not found.");
      }

      const now = new Date();

      const pad = (n: number) => n.toString().padStart(2, "0");

      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const militaryTime = `${hours}${minutes}`;

      const dateStr = `${pad(now.getMonth() + 1)}${pad(
        now.getDate()
      )}${pad(now.getFullYear())}-${militaryTime}`;

      const sanitizedPropertyName = String(targetPropertyName).replace(
        /\s+/g,
        "-"
      );

      uniqueId = `${sanitizedPropertyName}-${targetWorkCode}-${dateStr}`;

      let graphicsArray: Graphic[] = [];

      for (let o = 0; o < objectIds.length; o++) {
        graphicsArray.push(
          new Graphic({
            attributes: {
              [objectId.name]: objectIds[o],
              [status.name]: "Needs Review",
              [packageId.name]: uniqueId,
            },
          })
        );
      }

      let folderUrl = await axios.post(`${BoxApiUrl}/createfolder`, {
        folderName: uniqueId,
        organization: "OCPW_OANDM",
        folderId: parentFolder,
      });

      let editedFeatures = await featLayer.applyEdits({
        updateFeatures: graphicsArray,
      });

      if (editedFeatures?.updateFeatureResults.length > 0) {
        const updatedObjectIds = editedFeatures.updateFeatureResults
          .map((result: { objectId?: number }) => result.objectId)
          .filter(
            (id: number | undefined): id is number => id !== undefined
          );

        if (updatedObjectIds.length > 0) {
          await removeFromSelectionHandler(view, {
            layerId: featLayer.id,
            queryString: `objectId IN (${updatedObjectIds.join(",")})`,
          });

          await selectDataHandler(
            view,
            {
              layerId: featLayer.id,
              queryString: `objectId IN (${updatedObjectIds.join(",")})`,
            },
            false
          );
        }
      }

      if (folderUrl && folderUrl.data && folderUrl.data.Diagnostic.id) {
        setDialogMessage(
          <>
            Package created successfully! View the folder here:{" "}
            <a
              href={`https://ocgov.app.box.com/folder/${folderUrl.data.Diagnostic.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Box Folder
            </a>
          </>
        );
      } else {
        setDialogMessage(
          "Package created, but Box folder information is missing."
        );
      }

      setPackageId(uniqueId);
      setDialogOpen(true);

      console.log(editedFeatures);

      return editedFeatures;
    } catch (err: Error | any) {
      if (err instanceof Error) {
        setError(`Failed to create package. Check if folder already exists.
          Error: ${err.message}`);
      } else {
        setError("Failed to create package. An unknown error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Dialog
        open={!!error}
        onClose={() => {
          setError(null);
        }}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title" sx={{ color: "error.main" }}>
          Error
        </DialogTitle>
        <DialogContent>
          <Typography id="error-dialog-description">{error}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setError(null);
            }}
            color="primary"
            autoFocus
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title" sx={{ color: "warning.main" }}>
          Warning
        </DialogTitle>
        <DialogContent>
          <Typography id="error-dialog-description">
            You are about to create a Package do you wish to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmDialogOpen(false);
              onClick();
            }}
            color="primary"
            autoFocus
          >
            Continue
          </Button>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            color="primary"
            autoFocus
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setPackageId(null);
        }}
        aria-labelledby="success-dialog-title"
      >
        <DialogTitle id="success-dialog-title">Success</DialogTitle>
        <DialogContent>
          <Typography>{dialogMessage}</Typography>
          <Typography>{packageId}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            color="primary"
            autoFocus
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {loading && (
        <Backdrop sx={{ zIndex: 2000 }} open={true}>
          <CircularProgress size={120} thickness={4} />
        </Backdrop>
      )}
      <Button
        onClick={() => {
          setConfirmDialogOpen(true);
        }}
        size={"small"}
        disabled={loading || payload.length < 1}
        sx={{ fontSize: 10, height: "40px" }}
      >
        Create package
      </Button>
    </>
  );
};

export default CreatePackageBtn;
